import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import router from "../router";
import { useAuthStore } from "../store/auth";
import { SessionExpiredError } from "../errors/SessionExpiredError";

// For displaying session expiration toast
let toastInstance: any = null;

// Initialize toast instance (called from main.ts after app is created)
export function initializeApiToast(toast: any) {
  toastInstance = toast;
}

// Flag to ensure we only show session expired toast once
let sessionExpiredToastShown = false;

// Track last successful refresh/login time
let lastRefreshTime = 0;
let refreshPromise: Promise<boolean> | null = null;

// Access token TTL is 15 min (backend). Refresh proactively 2 min before expiry.
const ACCESS_TOKEN_LIFETIME_MS = 15 * 60 * 1000;
const TOKEN_REFRESH_BUFFER_MS = 2 * 60 * 1000;
const TOKEN_REFRESH_THRESHOLD_MS = ACCESS_TOKEN_LIFETIME_MS - TOKEN_REFRESH_BUFFER_MS;

// Cooldown: skip proactive refresh if last one was less than 2 minutes ago
const REFRESH_COOLDOWN_MS = TOKEN_REFRESH_BUFFER_MS;

/**
 * Called after login to mark the token as fresh.
 * Prevents unnecessary proactive refresh right after login.
 */
export function markTokenRefreshed() {
  lastRefreshTime = Date.now();
}

/**
 * Proactively refresh the auth token before navigation.
 * Skips refresh if the last one was recent (within REFRESH_COOLDOWN_MS).
 * Returns true if refresh succeeded (or was skipped), false if it failed.
 */
export async function proactiveTokenRefresh(): Promise<boolean> {
  // Skip if we refreshed recently
  if (Date.now() - lastRefreshTime < REFRESH_COOLDOWN_MS) {
    return true;
  }

  // If a refresh is already in progress, return the same promise
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      await axios.post('/api/auth/refresh', {}, { withCredentials: true });
      lastRefreshTime = Date.now();
      sessionExpiredToastShown = false;
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

const api = axios.create({
  baseURL: "/api",
  withCredentials: true, // Important pour les cookies d'authentification
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Flag to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// Function to notify all subscribers when token is refreshed
function onRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

// Function to add subscriber to refresh queue
function addRefreshSubscriber(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

// Add request interceptor to proactively refresh token before expiry
// and queue requests while a refresh is in progress
api.interceptors.request.use(async (config) => {
  const isAuthEndpoint = config.url?.includes('/auth/login') ||
                         config.url?.includes('/auth/logout') ||
                         config.url?.includes('/auth/refresh');

  // Proactively refresh if token is near expiry or unknown (before getting a 401)
  if (!isAuthEndpoint && !isRefreshing) {
    const tokenAge = Date.now() - lastRefreshTime;
    if (tokenAge > TOKEN_REFRESH_THRESHOLD_MS) {
      isRefreshing = true;
      try {
        await axios.post('/api/auth/refresh', {}, { withCredentials: true });
        lastRefreshTime = Date.now();
        sessionExpiredToastShown = false;
      } catch {
        // Let the request proceed - the response interceptor will handle the 401
      } finally {
        isRefreshing = false;
        onRefreshed('token-refreshed');
      }
    }
  }

  if (isRefreshing && !isAuthEndpoint) {
    // Wait for the refresh to complete before sending this request
    return new Promise((resolve) => {
      addRefreshSubscriber(() => {
        resolve(config);
      });
    });
  }

  return config;
});

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Check if error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh on login, logout, or refresh endpoints
      const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                            originalRequest.url?.includes('/auth/logout') ||
                            originalRequest.url?.includes('/auth/refresh');

      if (isAuthEndpoint) {
        // For auth endpoints, just clear local state and redirect to login
        if (router.currentRoute.value.path !== "/login") {
          const authStore = useAuthStore();
          authStore.clearLocalState();
          router.push("/login");
        }
        return Promise.reject(error);
      }

      // Mark this request as retried
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          // Try to refresh the token
          await axios.post('/api/auth/refresh', {}, { withCredentials: true });

          // Token refreshed successfully
          isRefreshing = false;
          lastRefreshTime = Date.now();
          sessionExpiredToastShown = false; // Reset flag on successful refresh
          onRefreshed('token-refreshed');

          // Retry the original request
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, user needs to log in again
          isRefreshing = false;
          refreshSubscribers = [];

          // Show session expired toast only once
          if (!sessionExpiredToastShown && toastInstance) {
            sessionExpiredToastShown = true;
            toastInstance.add({
              severity: 'warn',
              summary: 'Session expirée',
              detail: 'Votre session a expiré. Veuillez vous reconnecter.',
              life: 5000
            });
          }

          if (router.currentRoute.value.path !== "/login") {
            const authStore = useAuthStore();
            authStore.clearLocalState();
            router.push("/login");
          }

          // Reject with SessionExpiredError so components can identify it
          return Promise.reject(new SessionExpiredError('Refresh token expired'));
        }
      } else {
        // Another request is already refreshing, queue this request
        return new Promise((resolve) => {
          addRefreshSubscriber(() => {
            // Retry the original request after token is refreshed
            resolve(api(originalRequest));
          });
        });
      }
    }

    return Promise.reject(error);
  }
);

export default api;
